import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

const App = () => {
  const [botInfo, setBotInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Пусто → относительные пути (/api/...), их проксирует nginx того же origin.
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Получаем информацию о боте
      const botResponse = await axios.get(`${backendUrl}/api/bot/info`);
      setBotInfo(botResponse.data);
      
      // Получаем статистику
      const statsResponse = await axios.get(`${backendUrl}/api/users/stats`);
      setStats(statsResponse.data);
      
    } catch (err) {
      setError('Ошибка загрузки данных');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-xl text-gray-600">Загрузка данных...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <svg className="mx-auto h-16 w-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <p className="text-xl text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 bg-indigo-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-xl">🤖</span>
                </div>
              </div>
              <div className="ml-4">
                <h1 className="text-2xl font-bold text-gray-900">Nutrition Bot</h1>
                <p className="text-gray-600">Панель управления</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-gray-500">Статус бота</p>
                <p className="text-green-600 font-semibold flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  Активен
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'dashboard'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📊 Дашборд
            </button>
            <button
              onClick={() => setActiveTab('bot-info')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'bot-info'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🤖 Информация о боте
            </button>
            <button
              onClick={() => setActiveTab('features')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'features'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              ⚡ Функции
            </button>
            <button
              onClick={() => setActiveTab('guide')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'guide'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📖 Руководство
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {activeTab === 'dashboard' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {/* Stats Cards */}
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm">👥</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Всего пользователей</dt>
                        <dd className="text-lg font-medium text-gray-900">{stats?.total_users || 0}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm">🍽️</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Анализов проведено</dt>
                        <dd className="text-lg font-medium text-gray-900">{stats?.total_analyses || 0}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm">⚡</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Статус системы</dt>
                        <dd className="text-lg font-medium text-green-600">Активна</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Последняя активность</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">📊</span>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">Анализ питания</p>
                      <p className="text-xs text-gray-500">Новый анализ фото еды</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">2 мин назад</span>
                </div>
                
                <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">👤</span>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">Новый пользователь</p>
                      <p className="text-xs text-gray-500">Регистрация в системе</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">15 мин назад</span>
                </div>
                
                <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">🤖</span>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">Команда "Ботик"</p>
                      <p className="text-xs text-gray-500">Обработка команды в группе</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">1 час назад</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'bot-info' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-6">Информация о боте</h3>
              
              {botInfo && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <dl className="space-y-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">ID бота</dt>
                        <dd className="text-lg font-mono text-gray-900">{botInfo.bot_id}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Имя пользователя</dt>
                        <dd className="text-lg text-gray-900">@{botInfo.bot_username}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Отображаемое имя</dt>
                        <dd className="text-lg text-gray-900">{botInfo.bot_name}</dd>
                      </div>
                    </dl>
                  </div>
                  
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-4">Как добавить бота:</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                        <li>Откройте Telegram</li>
                        <li>Найдите @{botInfo.bot_username}</li>
                        <li>Нажмите "Начать" или отправьте /start</li>
                        <li>Отправьте фото еды для анализа</li>
                      </ol>
                    </div>
                    
                    <div className="mt-4">
                      <a
                        href={`https://t.me/${botInfo.bot_username}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                      >
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0C5.374 0 0 5.373 0 12s5.374 12 12 12 12-5.373 12-12S18.626 0 12 0zm5.568 8.16c-.169 1.858-.896 6.728-.896 6.728-.378 2.618-1.4 3.062-2.877 1.897l-1.595-1.28c-.896-.717-1.923-.717-2.819 0l-1.595 1.28c-1.477 1.165-2.499.721-2.877-1.897 0 0-.727-4.87-.896-6.728-.169-1.858.896-2.618 2.373-1.897l9.409 3.794c1.477.721 2.542 0 2.373-1.897z"/>
                        </svg>
                        Открыть в Telegram
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'features' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-6">Функции бота</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm">🍽️</span>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-md font-medium text-gray-900">Анализ питания</h4>
                      <p className="text-sm text-gray-600">Определение блюд, калорий и БЖУ по фото</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm">🤖</span>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-md font-medium text-gray-900">ИИ-помощник</h4>
                      <p className="text-sm text-gray-600">Персональные рекомендации по питанию</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm">📊</span>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-md font-medium text-gray-900">Статистика</h4>
                      <p className="text-sm text-gray-600">Отслеживание прогресса и привычек</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm">💬</span>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-md font-medium text-gray-900">Групповые чаты</h4>
                      <p className="text-sm text-gray-600">Работа в группах и каналах</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm">🗣️</span>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-md font-medium text-gray-900">Команды "Ботик"</h4>
                      <p className="text-sm text-gray-600">Обработка естественных команд</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm">🏁</span>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-md font-medium text-gray-900">Форумы и темы</h4>
                      <p className="text-sm text-gray-600">Поддержка Telegram Forums</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'guide' && (
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-6">Руководство пользователя</h3>
              
              <div className="prose max-w-none">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-4">🚀 Быстрый старт</h4>
                    <div className="space-y-3 text-sm text-gray-600">
                      <div className="flex items-start">
                        <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs mr-3">1</span>
                        <p>Отправьте команду /start боту</p>
                      </div>
                      <div className="flex items-start">
                        <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs mr-3">2</span>
                        <p>Сфотографируйте вашу еду</p>
                      </div>
                      <div className="flex items-start">
                        <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs mr-3">3</span>
                        <p>Отправьте фото боту</p>
                      </div>
                      <div className="flex items-start">
                        <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs mr-3">4</span>
                        <p>Получите анализ и рекомендации</p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-4">📱 Основные команды</h4>
                    <div className="space-y-2 text-sm">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <code className="text-blue-600">/start</code> - Запуск бота
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <code className="text-blue-600">/menu</code> - Главное меню
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <code className="text-blue-600">/help</code> - Помощь
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <code className="text-blue-600">Ботик + команда</code> - Естественные команды
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8">
                  <h4 className="text-md font-medium text-gray-900 mb-4">💡 Советы по использованию</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h5 className="font-medium text-blue-900 mb-2">Для качественного анализа:</h5>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• Фотографируйте при хорошем освещении</li>
                        <li>• Показывайте всю порцию</li>
                        <li>• Избегайте теней и бликов</li>
                        <li>• Фокусируйтесь на еде</li>
                      </ul>
                    </div>
                    
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h5 className="font-medium text-green-900 mb-2">В групповых чатах:</h5>
                      <ul className="text-sm text-green-800 space-y-1">
                        <li>• Используйте "Ботик" для команд</li>
                        <li>• Получайте подробные анализы</li>
                        <li>• Используйте кнопки для навигации</li>
                        <li>• Настраивайте автоудаление</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default App;